import { useEffect, useRef } from 'react'

interface Props {
  message: string
  show: boolean
  onHide: () => void
  durationMs?: number
  action?: { label: string; onClick: () => void }
}

/**
 * Small bottom toast. Mirrors the mockup helper. Auto-hides after `durationMs`.
 * Calling `show=true` re-arms the timer; toggling the message resets it.
 * If `action` is provided, the toast stays until clicked and shows a button
 * on the right (used for the "new version available" prompt).
 */
export function Toast({ message, show, onHide, durationMs = 2200, action }: Props) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!show) return
    if (action) return  // persistent toast while waiting for user action
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(onHide, durationMs)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [show, message, durationMs, onHide, action])

  return (
    <div
      className={`toast${show ? ' show' : ''}${action ? ' has-action' : ''}`}
      role="status"
      aria-live="polite"
    >
      <span className="toast-msg">{message}</span>
      {action && (
        <button
          className="toast-action"
          onClick={() => {
            action.onClick()
            onHide()
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  )
}
